// Process a card transaction
import React, { useCallback, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useApi } from "../../hooks/UseApi";
import {
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha
} from 'react-google-recaptcha-v3';
import { RECAPTCHA_API_KEY } from "../../env";

const FormProcessCard = (props) => {
  // populate form with saved billing information
  useEffect(() => {
    if (props.savedBilling && props.savedBilling.length !== 0) {
      reset(props.savedBilling);
    }
  }, [props.savedBilling]);

  // set up ReCaptcha
  const [captchaToken, setCaptchaToken] = useState("");
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const handleVerify = useCallback((token) => {
    setCaptchaToken(token);
  }, [setCaptchaToken]);
  useEffect(() => {
    setInterval(() => {
      setRefreshReCaptcha(r => !r);
    }, 90000);
  }, [setRefreshReCaptcha]);

  // set up API, Stripe, and Form hooks
  const { put, post } = useApi(onError);
  const stripe = useStripe();
  const elements = useElements();
  const methods = useForm({
    mode: "onBlur",
    defaultValues: props.fields,
  });
  const { handleSubmit, reset } = methods;

  const disableSubmit = () => {
    props.submit.current.disabled = true;
    props.loader.current.classList.add("show");
  };
  const resetSubmit = () => {
    props.loader.current.classList.remove("show");
    props.submit.current.disabled = false;
  };
  const onSubmit = async (formData) => {
    // report submit click and disable button
    gtag("event", "contribute_button_click");
    disableSubmit();

    // parse data
    const stripeFields = {
      name: formData.billing_name,
      address_line1: formData.billing_line1,
      address_line2: formData.billing_line2,
      address_city: formData.billing_city,
      address_state: formData.billing_state,
      address_zip: formData.billing_zip,
      address_country: formData.billing_country,
    };
    const longNowFields = {
      email: formData.email,
      billing_name: formData.billing_name,
      billing_line1: formData.billing_line1,
      billing_line2: formData.billing_line2,
      billing_city: formData.billing_city,
      billing_state: formData.billing_state,
      billing_zip: formData.billing_zip,
      billing_country: formData.billing_country,
    };
    if (props.action == "donate") {
      longNowFields.donation_amount = formData.donation_amount;
    } else if (props.action == "join") {
      longNowFields.cc_recurring =
        formData.membership_level != 4 ? formData.cc_recurring : "YEAR";
      longNowFields.membership_level = formData.membership_level;
      longNowFields.full_name = formData.full_name;
      longNowFields.shipping_name = formData.full_name;
      longNowFields.username = formData.username;
      longNowFields.password = formData.password;
      longNowFields.signup_reason = formData.reason;
      if (
        document.getElementById("same_as_billing") &&
        document.getElementById("same_as_billing").checked
      ) {
        longNowFields.shipping_line1 = formData.billing_line1;
        longNowFields.shipping_line2 = formData.billing_line2;
        longNowFields.shipping_city = formData.billing_city;
        longNowFields.shipping_state = formData.billing_state;
        longNowFields.shipping_zip = formData.billing_zip;
        longNowFields.shipping_country = formData.billing_country;
      } else {
        longNowFields.shipping_line1 = formData.shipping_line1;
        longNowFields.shipping_line2 = formData.shipping_line2;
        longNowFields.shipping_city = formData.shipping_city;
        longNowFields.shipping_state = formData.shipping_state;
        longNowFields.shipping_zip = formData.shipping_zip;
        longNowFields.shipping_country = formData.shipping_country;
      }
    }

    // WAIT FOR STRIPE
    if (!stripe || !elements) {
      return;
    }
    if (props.chosenMethod != "new" && props.savedBilling.card?.last4) {
      // SAVED BILLING METHOD: CHECK USERNAME
      if (props.member?.username) {
        longNowFields.use_saved_method = props.member.username;
        longNowFields.rc_token = captchaToken;
      }
    } else {
      // NEW BILLING METHOD: GET STRIPE TOKEN
      const result = await stripe.createToken(
        elements.getElement(CardNumberElement),
        stripeFields
      );
      if (result.error) {
        // STRIPE TOKEN ERROR
        if (result.error.type == "validation_error") {
          props.handleFormError(`${result.error.message} Please try again.`);
        } else {
          gtag("event", "contribute_stripe_error", {
            message: result.error.message,
          });
          props.handleFormError(
            "Something went wrong. You have not been charged. Please contact membership@longnow.org or try again later."
          );
        }
        resetSubmit();
      } else {
        // STRIPE TOKEN SUCCESS
        longNowFields.stripe_token = result.token.id;
        longNowFields.rc_token = captchaToken;
      }
    }

    // BILLING SETTINGS API CALL
    if (props.action == "settings") {
      put(
        `membership/user/${props.member.username}/billing`,
        longNowFields,
        "csrf"
      ).then((data) => {
        if (data) {
          // handle API success
          props.handleFormSuccess(true);
        }
      });
    } else {
      // JOIN/DONATE API CALL
      gtag("event", "contribute_stripe_success");
      post(`membership/`, longNowFields, "csrf").then((data) => {
        if (data) {
          // handle API success
          gtag("event", "contribute_form_success");
          if (data.membership_number) {
            props.handleNewMember(data.membership_number);
          }
          props.handleFormSuccess(true);
        }
      });
    }
  };
  function onError(error) {
    if (props.action == "join") {
      gtag("event", "contribute_form_error", {
        message: error,
      });
    }
    props.handleFormError(
      "Something went wrong. You have not been charged. Please contact membership@longnow.org or try again later."
    );
    for (const [field, message] of Object.entries(error)) {
      if (!field || field == "") {
        props.handleFormError(message);
      } else {
        methods.setError(
          field,
          {
            type: "api",
            message: message,
          },
          { shouldFocus: true }
        );
      }
    }
    resetSubmit();
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_API_KEY}>
      <GoogleReCaptcha
        onVerify={handleVerify} refreshReCaptcha={refreshReCaptcha} />

      <FormProvider {...methods}>
        <form id="Process_card" onSubmit={handleSubmit(onSubmit)} noValidate>
          {props.children}
        </form>
      </FormProvider>
    </GoogleReCaptchaProvider>
  );
};
export default FormProcessCard;
